import type {
  CreatePortalOrderBody,
  PortalCustomerQuoteDto,
  UpdatePortalOrderBody,
  PortalFileDto,
  PortalMeDto,
  PortalMessageDto,
  PortalOrderDto,
  PortalOrderListParams,
  PortalOrderListResponse,
  PortalOwnerDto,
  PortalProductRef,
  PlanningProjectTagDto,
  SetPortalQuoteStatusBody
} from '@malash-office/types';
import type { PlanningLookupDto } from '@malash-office/types';
import instanceAxios from '@/util/axios';
import { cleanParams } from '../planning/planningProjects.api';

/**
 * פורטל לקוחות ייצור — the customer's own two screens.
 *
 * `BASE` is `portal` and not `pvt/...`: this is a branch of its own on the server, with its own
 * middleware deciding which organisation the caller speaks for. Nothing here ever sends a
 * `productionCustomerId` for that reason — the one exception is the order form, where a contact who
 * speaks for two organisations picks between them and the server checks the choice against the
 * session.
 *
 * File links are **paths, not URLs**: the server answers a download with a redirect to a
 * short-lived signed link, so the address of a file never sits in a response.
 */

const BASE = 'portal';

export const fetchPortalMe = async (): Promise<PortalMeDto> => {
  const { data } = await instanceAxios.get<PortalMeDto>(`${BASE}/me`);
  return data;
};

export const fetchPortalOrders = async (params: PortalOrderListParams): Promise<PortalOrderListResponse> => {
  const { data } = await instanceAxios.get<PortalOrderListResponse>(`${BASE}/orders`, {
    params: cleanParams(params)
  });
  return data;
};

export const fetchPortalOrder = async (id: number): Promise<PortalOrderDto> => {
  const { data } = await instanceAxios.get<PortalOrderDto>(`${BASE}/orders/${id}`);
  return data;
};

export const createPortalOrder = async (body: CreatePortalOrderBody): Promise<PortalOrderDto> => {
  const { data } = await instanceAxios.post<PortalOrderDto>(`${BASE}/orders`, body);
  return data;
};

export const updatePortalOrder = async (id: number, body: UpdatePortalOrderBody): Promise<PortalOrderDto> => {
  const { data } = await instanceAxios.patch<PortalOrderDto>(`${BASE}/orders/${id}`, body);
  return data;
};

/** Soft-cancel. The order comes back marked מבוטל. */
export const cancelPortalOrder = async (id: number): Promise<PortalOrderDto> => {
  const { data } = await instanceAxios.delete<PortalOrderDto>(`${BASE}/orders/${id}`);
  return data;
};

/** The customer's answer to a quote. Answers with the whole order, redrawn. */
export const setPortalQuoteStatus = async (
  quoteId: number,
  body: SetPortalQuoteStatusBody
): Promise<PortalOrderDto> => {
  const { data } = await instanceAxios.post<PortalOrderDto>(`${BASE}/quotes/${quoteId}/status`, body);
  return data;
};

export const fetchPortalMessages = async (id: number): Promise<PortalMessageDto[]> => {
  const { data } = await instanceAxios.get<PortalMessageDto[]>(`${BASE}/orders/${id}/messages`);
  return data;
};

export const sendPortalMessage = async (id: number, body: string): Promise<PortalMessageDto> => {
  const { data } = await instanceAxios.post<PortalMessageDto>(`${BASE}/orders/${id}/messages`, { body });
  return data;
};

export const markPortalThreadRead = async (id: number): Promise<void> => {
  await instanceAxios.post(`${BASE}/orders/${id}/messages/read`, {});
};

export const fetchPortalFiles = async (id: number): Promise<PortalFileDto[]> => {
  const { data } = await instanceAxios.get<PortalFileDto[]>(`${BASE}/orders/${id}/files`);
  return data;
};

export const deletePortalFile = async (id: number, groupId: number): Promise<void> => {
  await instanceAxios.delete(`${BASE}/orders/${id}/files/${groupId}`);
};

export const uploadPortalFile = async (id: number, file: File, name: string): Promise<PortalFileDto[]> => {
  const form = new FormData();
  form.append('file', file);
  form.append('name', name);
  const { data } = await instanceAxios.post<PortalFileDto[]>(`${BASE}/orders/${id}/files`, form);
  return data;
};

export const fetchPortalProjectTypes = async (): Promise<PlanningLookupDto[]> => {
  const { data } = await instanceAxios.get<PlanningLookupDto[]>(`${BASE}/project-types`);
  return data;
};

export const fetchPortalCategories = async (): Promise<PlanningLookupDto[]> => {
  const { data } = await instanceAxios.get<PlanningLookupDto[]>(`${BASE}/project-categories`);
  return data;
};

export const createPortalCategory = async (name: string): Promise<PlanningLookupDto> => {
  const { data } = await instanceAxios.post<PlanningLookupDto>(`${BASE}/project-categories`, { name });
  return data;
};

export const fetchPortalTags = async (): Promise<PlanningProjectTagDto[]> => {
  const { data } = await instanceAxios.get<PlanningProjectTagDto[]>(`${BASE}/project-tags`);
  return data;
};

export const createPortalTag = async (name: string): Promise<PlanningProjectTagDto> => {
  const { data } = await instanceAxios.post<PlanningProjectTagDto>(`${BASE}/project-tags`, { name });
  return data;
};

/** The team members on their own orders — the "אחראי" filter. */
export const fetchPortalOwners = async (): Promise<PortalOwnerDto[]> => {
  const { data } = await instanceAxios.get<PortalOwnerDto[]>(`${BASE}/owners`);
  return data;
};

/** Every product made for their organisation — the picker for a new round or an upgrade. */
export const fetchPortalProducts = async (): Promise<PortalProductRef[]> => {
  const { data } = await instanceAxios.get<PortalProductRef[]>(`${BASE}/products`);
  return data;
};

/**
 * The address of a file's bytes.
 *
 * `downloadPath` comes from the server already scoped; all this adds is the API base, because the
 * browser is the right thing to hand a download to and it needs an absolute address to follow.
 */
export const portalDownloadUrl = (downloadPath: string): string =>
  `${instanceAxios.defaults.baseURL ?? ''}${downloadPath}`;

export type { PortalCustomerQuoteDto };
